import { useMemo } from 'react';
import {
  EventHeaderIconWithHoverEffect,
  EventHeaderMyEntityToggleItem,
  EventHeaderSidePanelSwiper,
  EventHeaderTableColumnAction,
} from 'src/components/Events/EventHeader';
import {
  EventHeaderMarketplacesWithMetrics,
  EventHeaderMetricsType,
} from 'src/components/Events/EventHeader/EventHeaderMarketplaceMetrics';
import { EventHeaderMetrics } from 'src/components/Events/EventHeader/EventHeaderMarketplaceMetrics.types';
import { TabItem } from 'src/components/Events/EventPage/EventPageTabs';
import { BulkActionToggleButton } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/EventHeader/EventActions/BulkAction';
import { useMarketplaceEventInfo } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/InventoryEventLocalContext.hooks';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ToggleGroup, ToggleItem } from 'src/core/ui/ToggleGroup';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CustomSalesColumn } from 'src/utils/columns/sales/salesCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { formatPercent } from 'src/utils/numberFormatter';
import { SectionType } from 'src/utils/types/sectionType';
import { Event, Feature, SaleMetrics, Venue } from 'src/WebApiController';

import { SaleEventTabs } from '../SaleEventPage';
import * as styles from './SalesEventPageHeaderV2.css';

export const SalesEventPageHeaderWithNav = ({
  event,
  venue,
  tabs,
  activeTab,
  setActiveTab,
}: {
  event: Event;
  venue?: Venue;
  activeTab: SaleEventTabs;
  setActiveTab: (tab: SaleEventTabs) => void;
  tabs: TabItem<SaleEventTabs>[];
}) => {
  const { enableSelectionByDefault } = useMultiSelectionContext();
  const { marketplaceEventInfo, refetchMarketplaceEventInfo } =
    useMarketplaceEventInfo(event);

  const hasSalesTableV2Feature = useUserHasFeature(Feature.SalesTableV2);

  const isMobile = useMatchMedia('mobile');

  const { eventMetrics: eventMetricsMap } =
    useCatalogMetricsContext<SaleMetrics>();
  const eventMetrics = eventMetricsMap?.[event?.virtId ?? ''];

  const toggleGroupOptions = useMemo(() => {
    return (tabs.filter((tab) => tab.value !== SaleEventTabs.Sales) ?? [])
      .map(({ value, Icon, contentId, useStroke }) => {
        if (!Icon || !contentId) {
          return null;
        }
        return {
          value,
          children: (
            <EventHeaderIconWithHoverEffect
              IconComponent={Icon!}
              selected={value === activeTab}
              useStroke={useStroke}
              contentId={contentId}
              size={vars.iconSize.l}
            />
          ),
        };
      })
      .filter(
        (item: ToggleItem | null): item is ToggleItem => item != null
      ) as ToggleItem[];
  }, [activeTab, tabs]);

  const isOnMyPurchases = useMemo(
    () => activeTab === SaleEventTabs.Sales,
    [activeTab]
  );

  const salesMetricsStats = useMemo((): EventHeaderMetrics[] => {
    const filled = !eventMetrics?.saleUnfilled
      ? 0
      : (eventMetrics?.qtySold || 0) / eventMetrics?.saleUnfilled;
    return [
      {
        label: ContentId.Margin,
        value: formatPercent(eventMetrics?.marg || 0),
        direction: isMobile ? 'column' : 'row',
      },
      {
        label: ContentId.Filled,
        value: `${filled}/${eventMetrics?.qtySold || 0}`,
        direction: isMobile ? 'column' : 'row',
      },
      {
        label: ContentId.Sales,
        value: eventMetrics?.qtySold || 0,
        direction: isMobile ? 'column' : 'row',
      },
    ];
  }, [
    eventMetrics?.marg,
    eventMetrics?.qtySold,
    eventMetrics?.saleUnfilled,
    isMobile,
  ]);

  const isOnMySales = useMemo(
    () => activeTab === SaleEventTabs.Sales,
    [activeTab]
  );

  if (isMobile) {
    return (
      <Stack direction="column" gap="m">
        <EventHeaderMarketplacesWithMetrics
          justifyContent="spaceBetween"
          className={styles.eventHeaderNavCard}
          event={event}
          venue={venue}
          marketplaceEventInfo={marketplaceEventInfo}
          onRefetchMarketplaceEventInfo={refetchMarketplaceEventInfo}
          metrics={{
            type: EventHeaderMetricsType.Stats,
            stats: salesMetricsStats,
          }}
        />
        {isOnMySales && (
          <Stack
            direction="row"
            justifyContent="spaceBetween"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" gap="m">
              {event?.virtId && !enableSelectionByDefault && (
                <BulkActionToggleButton groupId={event.virtId} />
              )}
            </Stack>
            <EventHeaderSidePanelSwiper contentId={ContentId.ViewPurchase} />
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      direction="responsive"
      alignItems="center"
      justifyContent="spaceBetween"
      className={styles.eventHeaderCard}
    >
      <Stack direction="row" alignItems="center" gap="m">
        <EventHeaderMyEntityToggleItem
          selected={isOnMySales}
          contentId={ContentId.MySales}
          onClick={() => setActiveTab(SaleEventTabs.Sales)}
        />
        {!enableSelectionByDefault && (
          <BulkActionToggleButton groupId={event.virtId} />
        )}
        <ToggleGroup
          value={activeTab}
          itemClassName={styles.toggleGroupItemParent}
          itemSelectedClassName={styles.toggleGroupItemSelected}
          onValueChange={(value) => {
            if (value) {
              setActiveTab(value as SaleEventTabs);
            }
          }}
          options={toggleGroupOptions}
        />

        {!hasSalesTableV2Feature && isOnMyPurchases && (
          <EventHeaderTableColumnAction<CustomSalesColumn>
            sectionType={SectionType.Sales}
          />
        )}
      </Stack>
      <EventHeaderMarketplacesWithMetrics
        gap="m"
        event={event}
        venue={venue}
        marketplaceEventInfo={marketplaceEventInfo}
        onRefetchMarketplaceEventInfo={refetchMarketplaceEventInfo}
        metrics={{
          type: EventHeaderMetricsType.Stats,
          stats: salesMetricsStats,
        }}
      />
    </Stack>
  );
};
